/**
 * TopNav - Main Application Navigation
 * 
 * Core Hardening Phase 2: Plugin-Driven Navigation
 * 
 * Uses NavigationRegistry and PermissionRegistry for fully plugin-driven navigation.
 * All navigation items come from the registry, not hardcoded config.
 * 
 * Features:
 * - Plugin-driven navigation items
 * - Permission-based visibility
 * - Responsive design with mobile sidebar
 * - CSS classes with design tokens (no inline styles)
 * - Proper ARIA attributes for accessibility
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useOrderedNavigation } from '../plugins/registries/navigation-registry';
import { usePermissions } from '../plugins/registries/permission-registry';
import type { NavigationCategory } from '../plugins/registries/navigation-registry';
import neuroLogo from '../assets/Neuro.png';
import { NotificationBell, NotificationCenter } from './notifications';
import { Icon } from './Icon';
import { useFocusTrap } from '../hooks/useAccessibility';
import { useFrequentPages, useFrequentPagesTracker, FREQUENT_PAGES_LIMIT } from '../hooks/useFrequentPages';
import type { FrequentPageLink } from '../hooks/useFrequentPages';
// Phase 11AA (P1-5): Global slot rendering for plugin UI contributions
import { SlotRenderer } from '@neuro/plugin-sdk/ui';
import './Navigation.css';

/**
 * FrequentNavDropdown
 *
 * A "Frequent" quick-nav entry for the top nav. Renders a button styled
 * like the category dropdowns; when opened it lists the user's top 10
 * most-visited pages (see {@link useFrequentPages}). Visits are tracked
 * automatically on every route change by {@link useFrequentPagesTracker}.
 *
 * The dropdown is hidden entirely until the user has at least one recorded
 * visit so the nav doesn't show an empty "Frequent" entry on first run.
 */
interface FrequentNavDropdownProps {
  pages: FrequentPageLink[];
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}

function FrequentNavDropdown({ pages, open, onToggle, onClose }: FrequentNavDropdownProps) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [style, setStyle] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) {
      setStyle({});
      return;
    }
    const button = buttonRef.current;
    if (!button) return;
    const rect = button.getBoundingClientRect();
    const minWidth = Math.max(rect.width, 220);
    const gutter = 8;
    const left = Math.min(
      Math.max(rect.left, gutter),
      Math.max(gutter, window.innerWidth - minWidth - gutter),
    );
    setStyle({
      '--top-nav-dropdown-left': `${left}px`,
      '--top-nav-dropdown-top': `${rect.bottom + 6}px`,
      '--top-nav-dropdown-min-width': `${minWidth}px`,
    });
  }, [open]);

  // Close on outside click while open.
  useEffect(() => {
    if (!open) return;
    const handle = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (buttonRef.current && buttonRef.current.contains(target)) return;
      const dropdown = document.querySelector('.top-nav__frequent-dropdown');
      if (dropdown && dropdown.contains(target)) return;
      onClose();
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open, onClose]);

  // Don't render the entry at all until the user has visited pages.
  if (pages.length === 0) return null;

  return (
    <div className="top-nav__category top-nav__frequent">
      <button
        ref={buttonRef}
        type="button"
        className="top-nav__category-btn top-nav__nav-item ds-topnav-item"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={onToggle}
        title="Pages you visit most"
      >
        <Icon name="clock" size={18} className="top-nav__icon ds-topnav-icon" />
        <span className="top-nav__item-label">Frequent</span>
        <span className="top-nav__category-arrow" aria-hidden="true">▼</span>
      </button>

      {open && (
        <div
          className="top-nav__dropdown top-nav__frequent-dropdown"
          role="menu"
          style={style as React.CSSProperties}
        >
          <div className="top-nav__frequent-header">
            <span>Frequently visited</span>
            <span className="top-nav__frequent-count">{pages.length}</span>
          </div>
          {pages.map((page) => (
            <NavLink
              key={page.path}
              to={page.path}
              className={({ isActive }) =>
                `top-nav__dropdown-item top-nav__frequent-item ${isActive ? 'top-nav__dropdown-item--active' : ''}`
              }
              role="menuitem"
              title={page.label}
              onClick={onClose}
            >
              <NavIcon icon={page.icon} className="top-nav__dropdown-icon" />
              <span className="top-nav__frequent-label">{page.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}
export const NAVBAR_HEIGHT = 64;
const DESKTOP_NAV_SCROLL_STEP = 240;

// Custom hook to detect mobile viewport
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);
  
  return isMobile;
}

/**
 * Get icon for navigation item
 * Uses the unified Icon component with color support
 */
function NavIcon({ icon, className = 'top-nav__icon' }: { icon: string | undefined; className?: string }) {
  if (!icon) return null;
  return <Icon name={icon} size={18} colorful={true} className={className} />;
}

export default function TopNav() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [frequentDropdownOpen, setFrequentDropdownOpen] = useState(false);
  const [desktopNavScrollState, setDesktopNavScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
  });
  const [dropdownStyle, setDropdownStyle] = useState<Record<string, string>>({});
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownTimeoutRef = useRef<number | null>(null);
  const navRef = useRef<HTMLElement>(null);
  const desktopNavViewportRef = useRef<HTMLElement>(null);
  const categoryButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const isMobile = useIsMobile();
  // Focus trap for mobile sidebar — traps focus when sidebar is open
  const mobileSidebarRef = useFocusTrap<HTMLDivElement>(mobileMenuOpen && isMobile, {
    autoFocus: true,
  });

  const { token, userId, clearAuth } = useAuthStore();

  // Track page visits for the "Frequent" quick-nav. Mounting the tracker
  // inside TopNav ensures it runs once for the lifetime of the app shell
  // and observes every route change via useLocation().
  useFrequentPagesTracker();
  const frequentPages = useFrequentPages(FREQUENT_PAGES_LIMIT);
  
  // Always call hooks unconditionally (React rules of hooks)
  const { permissions: loadedPermissions } = usePermissions();
  // Only use permissions if authenticated to prevent 401 loop
  const permissions = token ? loadedPermissions : [];
  
  // Wave 22.8: Unified ordered navigation (already filtered by permissions) —
  // one A–Z list rendered by both the desktop bar and the mobile sidebar.
  // Don't block navigation display on permissions loading — core items have no
  // requiredPermissions, so they render immediately. Only API-extended items with
  // permission requirements will be filtered once permissions finish loading.
  const { entries: orderedEntries, isLoading } = useOrderedNavigation(permissions);

  const orderedEntryKey = useMemo(
    () => orderedEntries.map((entry) => `${entry.type}:${entry.data.id}`).join('|'),
    [orderedEntries]
  );

  // Close mobile menu and frequent dropdown on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
    setFrequentDropdownOpen(false);
  }, [location.pathname]);

  // Close dropdown when clicking outside the nav
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
        setFrequentDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  const updateDesktopNavOverflow = useCallback(() => {
    const viewport = desktopNavViewportRef.current;
    if (!viewport) {
      setDesktopNavScrollState((prev) => (
        prev.canScrollLeft || prev.canScrollRight
          ? { canScrollLeft: false, canScrollRight: false }
          : prev
      ));
      return;
    }

    const epsilon = 1;
    const nextState = {
      canScrollLeft: viewport.scrollLeft > epsilon,
      canScrollRight: viewport.scrollLeft + viewport.clientWidth < viewport.scrollWidth - epsilon,
    };

    setDesktopNavScrollState((prev) => (
      prev.canScrollLeft === nextState.canScrollLeft && prev.canScrollRight === nextState.canScrollRight
        ? prev
        : nextState
    ));
  }, []);

  const updateDropdownPosition = useCallback((categoryId: string | null) => {
    if (!categoryId) {
      setDropdownStyle({});
      return;
    }

    const button = categoryButtonRefs.current[categoryId];
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const minWidth = Math.max(rect.width, 220);
    const gutter = 8;
    const left = Math.min(
      Math.max(rect.left, gutter),
      Math.max(gutter, window.innerWidth - minWidth - gutter)
    );

    setDropdownStyle({
      '--top-nav-dropdown-left': `${left}px`,
      '--top-nav-dropdown-top': `${rect.bottom + 6}px`,
      '--top-nav-dropdown-min-width': `${minWidth}px`,
    });
  }, []);

  const scrollDesktopNav = useCallback((direction: -1 | 1) => {
    const viewport = desktopNavViewportRef.current;
    if (!viewport) return;

    viewport.scrollBy({
      left: direction * Math.max(DESKTOP_NAV_SCROLL_STEP, viewport.clientWidth * 0.75),
      behavior: 'smooth',
    });
  }, []);

  const handleMouseEnter = (categoryId: string) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setOpenDropdown(categoryId);
    updateDropdownPosition(categoryId);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = window.setTimeout(() => {
      setOpenDropdown(null);
    }, 150);
  };

  useEffect(() => {
    if (!token || isMobile) {
      setDesktopNavScrollState({ canScrollLeft: false, canScrollRight: false });
      return;
    }

    const viewport = desktopNavViewportRef.current;
    if (!viewport) return;

    const handleScroll = () => updateDesktopNavOverflow();
    const handleResize = () => updateDesktopNavOverflow();

    updateDesktopNavOverflow();

    viewport.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(viewport);
      if (viewport.firstElementChild instanceof HTMLElement) {
        resizeObserver.observe(viewport.firstElementChild);
      }
    }

    return () => {
      viewport.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      resizeObserver?.disconnect();
    };
  }, [isLoading, isMobile, location.pathname, orderedEntryKey, token, updateDesktopNavOverflow]);

  useEffect(() => {
    if (!openDropdown || isMobile) return;

    const handleReposition = () => updateDropdownPosition(openDropdown);
    handleReposition();

    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [isMobile, openDropdown, updateDropdownPosition]);

  // Check if any item (or child) in category is active
  const isCategoryActive = (category: NavigationCategory) => {
    return category.items?.some(item => {
      if (location.pathname.startsWith(item.path)) return true;
      return item.children?.some(child => location.pathname.startsWith(child.path)) ?? false;
    }) ?? false;
  };

  const hasDesktopNavOverflow = desktopNavScrollState.canScrollLeft || desktopNavScrollState.canScrollRight;

  return (
    <header className="top-nav ds-topnav" ref={navRef}>
      {/* Logo */}
      <div className="top-nav__logo">
        <img src={neuroLogo} alt="Neuro Logo" className="top-nav__logo-img" />
        <span className="top-nav__logo-text">Neuro</span>
      </div>

      {/* Desktop Navigation — Wave 22.8: Single-pass unified rendering */}
      {token && !isMobile && (
        <div className="top-nav__desktop-nav-shell">
          {hasDesktopNavOverflow && (
            <button
              type="button"
              className="top-nav__scroll-btn top-nav__scroll-btn--left"
              aria-label="Scroll navigation left"
              onClick={() => scrollDesktopNav(-1)}
              disabled={!desktopNavScrollState.canScrollLeft}
            >
              <Icon name="chevron-left" size={18} />
            </button>
          )}

          <nav
            ref={desktopNavViewportRef}
            className="top-nav__desktop-nav ds-topnav__nav"
            aria-label="Main navigation"
          >
            {isLoading ? (
              <div className="top-nav__loading">Loading...</div>
            ) : (
              <div className="top-nav__desktop-nav-track">
                <FrequentNavDropdown
                  pages={frequentPages}
                  open={frequentDropdownOpen}
                  onToggle={() => {
                    setOpenDropdown(null);
                    setFrequentDropdownOpen((prev) => !prev);
                  }}
                  onClose={() => setFrequentDropdownOpen(false)}
                />
                {orderedEntries.map((entry) => {
                  if (entry.type === 'item') {
                    // Standalone item (Dashboard, Chat, or plugin item)
                    const item = entry.data;
                    return (
                      <NavLink
                        key={item.id}
                        to={item.path}
                        className={({ isActive }) =>
                          `top-nav__category-btn top-nav__nav-item ds-topnav-item ${isActive ? 'top-nav__category-btn--active ds-topnav-active' : ''}`
                        }
                        title={item.tooltip}
                      >
                        <NavIcon icon={item.icon} className="top-nav__icon ds-topnav-icon" />
                        <span className="top-nav__item-label">{item.label}</span>
                      </NavLink>
                    );
                  }

                  // Category dropdown (plugin category, Knowledge, or System)
                  const category = entry.data;
                  return (
                    <div
                      key={category.id}
                      className="top-nav__category"
                      onMouseEnter={() => handleMouseEnter(category.id)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <button
                        ref={(node) => {
                          categoryButtonRefs.current[category.id] = node;
                        }}
                        className={`top-nav__category-btn top-nav__nav-item ds-topnav-item ${isCategoryActive(category) ? 'top-nav__category-btn--active ds-topnav-active' : ''}`}
                        aria-expanded={openDropdown === category.id}
                        aria-haspopup="true"
                        onClick={() => {
                          if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
                          const nextDropdown = openDropdown === category.id ? null : category.id;
                          setOpenDropdown(nextDropdown);
                          updateDropdownPosition(nextDropdown);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
                            const nextDropdown = openDropdown === category.id ? null : category.id;
                            setOpenDropdown(nextDropdown);
                            updateDropdownPosition(nextDropdown);
                          } else if (e.key === 'Escape') {
                            setOpenDropdown(null);
                          }
                        }}
                      >
                        <NavIcon icon={category.icon} className="top-nav__icon ds-topnav-icon" />
                        <span className="top-nav__item-label">{category.title}</span>
                        <span className="top-nav__category-arrow" aria-hidden="true">▼</span>
                      </button>

                      {/* Dropdown Menu */}
                      {openDropdown === category.id && category.items && (
                        <div
                          className="top-nav__dropdown"
                          role="menu"
                          onMouseEnter={() => handleMouseEnter(category.id)}
                          onMouseLeave={handleMouseLeave}
                          style={dropdownStyle as React.CSSProperties}
                        >
                          {category.items.map((item) => (
                            <div key={item.id} className="top-nav__dropdown-group">
                              {/* Primary item */}
                              <NavLink
                                to={item.path}
                                className={({ isActive }) =>
                                  `top-nav__dropdown-item ${isActive ? 'top-nav__dropdown-item--active' : ''}`
                                }
                                role="menuitem"
                                title={item.tooltip}
                              >
                                <NavIcon icon={item.icon} className="top-nav__dropdown-icon" />
                                <span>{item.label}</span>
                              </NavLink>
                              {/* Children */}
                              {item.children && item.children.length > 0 && (
                                <div className="top-nav__dropdown-children">
                                  {item.children.map((child) => (
                                    <NavLink
                                      key={child.id}
                                      to={child.path}
                                      className={({ isActive }) =>
                                        `top-nav__dropdown-item top-nav__dropdown-item--child ${isActive ? 'top-nav__dropdown-item--active' : ''}`
                                      }
                                      role="menuitem"
                                      title={child.tooltip}
                                    >
                                      <NavIcon icon={child.icon} className="top-nav__dropdown-icon" />
                                      <span>{child.label}</span>
                                    </NavLink>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </nav>

          {hasDesktopNavOverflow && (
            <button
              type="button"
              className="top-nav__scroll-btn top-nav__scroll-btn--right"
              aria-label="Scroll navigation right"
              onClick={() => scrollDesktopNav(1)}
              disabled={!desktopNavScrollState.canScrollRight}
            >
              <Icon name="chevron-right" size={18} />
            </button>
          )}
        </div>
      )}

      {/* Right side: Actions */}
      <div className="top-nav__actions">
        {/* Phase 11AA (P1-5): Global header slot for plugin toolbar contributions */}
        <SlotRenderer slotId="global.header" className="top-nav__plugin-slot" inline />

        {token && (
          <>
            {/* Notification Bell */}
            {!isMobile && (
              <div className="top-nav__notification-wrapper">
                <NotificationBell className="top-nav__notification-btn" />
                <NotificationCenter />
              </div>
            )}

            {/* Mobile menu toggle */}
            {isMobile && (
              <button
                className="top-nav__mobile-toggle"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileMenuOpen}
              >
                {mobileMenuOpen ? '✕' : '☰'}
              </button>
            )}

            {/* Desktop Logout button */}
            {!isMobile && (
              <button className="top-nav__logout-btn" onClick={handleLogout}>
                Log out
              </button>
            )}

            {/* User badge */}
            {!isMobile && (
              <div className="top-nav__user-badge" title={userId || ''}>
                {userId || '—'}
              </div>
            )}
          </>
        )}
      </div>

      {/* Mobile Sidebar Overlay */}
      {token && isMobile && mobileMenuOpen && (
        <div
          className="top-nav__mobile-overlay"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile Sidebar */}
      {token && isMobile && (
        <div
          ref={mobileSidebarRef}
          className={`top-nav__mobile-sidebar ${mobileMenuOpen ? 'top-nav__mobile-sidebar--open' : ''}`}
          aria-hidden={!mobileMenuOpen}
          role="dialog"
          aria-label="Navigation menu"
        >
          {/* Sidebar Header */}
          <div className="top-nav__mobile-header">
            <div className="top-nav__logo">
              <img src={neuroLogo} alt="Neuro Logo" className="top-nav__mobile-logo-img" />
              <span className="top-nav__mobile-logo-text">Neuro</span>
            </div>
            <button
              className="top-nav__mobile-close"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              ✕
            </button>
          </div>

          {/* Mobile Navigation */}
          <nav className="top-nav__mobile-nav" aria-label="Mobile navigation">
            {isLoading ? (
              <div className="top-nav__loading">Loading...</div>
            ) : (
              <>
                {/* Frequent Pages */}
                {frequentPages.length > 0 && (
                  <div className="top-nav__mobile-category">
                    <div className="top-nav__mobile-category-header">
                      <Icon name="clock" size={18} className="top-nav__mobile-link-icon" />
                      <span>Frequent</span>
                    </div>
                    <div>
                      {frequentPages.map((page) => (
                        <NavLink
                          key={`frequent-${page.path}`}
                          to={page.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className={({ isActive }) =>
                            `top-nav__mobile-link ${isActive ? 'top-nav__mobile-link--active' : ''}`
                          }
                        >
                          <NavIcon icon={page.icon} className="top-nav__mobile-link-icon" />
                          <span>{page.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  </div>
                )}

                {/* Same A–Z order as the desktop bar: standalone items + categories */}
                {orderedEntries.map((entry) => {
                  if (entry.type === 'item') {
                    const item = entry.data;
                    return (
                      <NavLink
                        key={item.id}
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={({ isActive }) =>
                          `top-nav__mobile-link ${isActive ? 'top-nav__mobile-link--active' : ''}`
                        }
                      >
                        <NavIcon icon={item.icon} className="top-nav__mobile-link-icon" />
                        <span>{item.label}</span>
                      </NavLink>
                    );
                  }

                  const category = entry.data;
                  return (
                    <div key={category.id} className="top-nav__mobile-category">
                      <div className="top-nav__mobile-category-header">
                        <NavIcon icon={category.icon} className="top-nav__mobile-link-icon" />
                        <span>{category.title}</span>
                      </div>
                      <div>
                        {category.items?.map((item) => (
                          <div key={item.id}>
                            <NavLink
                              to={item.path}
                              onClick={() => setMobileMenuOpen(false)}
                              className={({ isActive }) =>
                                `top-nav__mobile-link ${isActive ? 'top-nav__mobile-link--active' : ''}`
                              }
                            >
                              <NavIcon icon={item.icon} className="top-nav__mobile-link-icon" />
                              <span>{item.label}</span>
                            </NavLink>
                            {item.children && item.children.length > 0 && (
                              <div className="top-nav__mobile-children">
                                {item.children.map((child) => (
                                  <NavLink
                                    key={child.id}
                                    to={child.path}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className={({ isActive }) =>
                                      `top-nav__mobile-link top-nav__mobile-link--child ${isActive ? 'top-nav__mobile-link--active' : ''}`
                                    }
                                  >
                                    <NavIcon icon={child.icon} className="top-nav__mobile-link-icon" />
                                    <span>{child.label}</span>
                                  </NavLink>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </nav>

          {/* Sidebar Footer */}
          <div className="top-nav__mobile-footer">
            <button className="top-nav__mobile-logout" onClick={handleLogout}>
              Log out
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
